import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Star, CaretLeft, CaretRight, Quotes } from '@phosphor-icons/react';

const testimonials = [
  {
    id: 1,
    name: 'Gary Thompson',
    location: 'Chittaway Bay',
    text: 'Brett delivered our pub renovation on time and on budget. He was on site every day, coordinated every trade, and nothing was ever a hassle. Couldn\'t recommend Rimsie Constructions Pty Ltd enough.',
    rating: 5,
  },
  {
    id: 2,
    name: 'Michelle & Dave Harrington',
    location: 'The Entrance North',
    text: 'From the first meeting to handover, Brett made building our new home straightforward. He kept us in the loop the whole way through and the finish quality was spot on. Absolute legend.',
    rating: 5,
  },
  {
    id: 3,
    name: 'Steve Oakley',
    location: 'Gosford',
    text: 'We\'ve used Brett across three of our venues for gaming room fitouts. Every single time he delivers — on schedule, minimal disruption to trading, and the finished product always looks top shelf.',
    rating: 5,
  },
];

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="section-padding bg-secondary-bg"
      ref={ref}
    >
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline mb-4">Testimonials</p>
          <h2
            data-testid="testimonials-heading"
            className="text-4xl sm:text-5xl font-heading font-light text-heading"
          >
            What Clients Say
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative">
            <div className="bg-primary-bg border border-border-subtle p-8 md:p-12 text-center">
              <Quotes size={48} weight="fill" className="text-accent/30 mx-auto mb-6" />

              <div className="flex justify-center gap-1 mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star key={`star-${i}`} size={20} weight="fill" className="text-accent" />
                ))}
              </div>

              <p className="text-heading text-lg md:text-xl leading-relaxed mb-8 italic">
                "{testimonials[currentIndex].text}"
              </p>

              <div>
                <p className="text-heading font-heading text-lg">
                  {testimonials[currentIndex].name}
                </p>
                <p className="text-body-muted text-sm">
                  {testimonials[currentIndex].location}
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-border-subtle">
                <p className="text-accent text-xs">
                  * Placeholder testimonials — Real client quotes to be added
                </p>
              </div>
            </div>

            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={prevSlide}
                data-testid="testimonial-prev"
                className="w-12 h-12 flex items-center justify-center border border-border-subtle text-heading hover:border-accent hover:text-accent transition-colors"
                aria-label="Previous testimonial"
              >
                <CaretLeft size={24} />
              </button>

              <div className="flex items-center gap-2">
                {testimonials.map((t, index) => (
                  <button
                    key={t.id}
                    onClick={() => setCurrentIndex(index)}
                    data-testid={`testimonial-dot-${index}`}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? 'bg-accent w-6' : 'bg-border-subtle'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                data-testid="testimonial-next"
                className="w-12 h-12 flex items-center justify-center border border-border-subtle text-heading hover:border-accent hover:text-accent transition-colors"
                aria-label="Next testimonial"
              >
                <CaretRight size={24} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
